/**
 * components/riskTerminal/RiskTerminalScreen.jsx
 *
 * The live Risk Terminal: the panel and the watchlist pushed over one
 * server-sent-events stream (upstox-054: liveStream.js, usePanel,
 * useWatchlist), rendered by the ported design (RiskTerminal).
 *
 * upstox-067: one screen for every account the user may see. The accounts come from
 * GET /upstox/accounts; the screen opens on the one he last chose in this browser (his own
 * Upstox if that is gone or storage is blocked) and never changes account by itself. Choosing
 * another remounts the live part, so the old stream is closed, the new one opened with
 * ?account=<id>, and nothing of the old account is left on the screen while the new loads.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import RiskTerminal from './RiskTerminal';
import PhoneTerminal from './phone/PhoneTerminal';
import usePhone from './phone/usePhone';
import usePanel from './usePanel';
import useWatchlist from './useWatchlist';
import { fetchAccounts, setActiveAccount, readRemembered, writeRemembered, chooseInitial } from './accounts';

function Live({ accounts, account, onSwitch }) {
  const rootRef = useRef(null);
  const { panel, fetchedAt, error, now, refresh, stream } = usePanel(account.id);
  const { watch, watchError, refreshWatch } = useWatchlist();          // upstox-054: pushed
  const phone = usePhone();                                             // upstox-076: below 600 px the phone layout; the same data and calls
  if (phone) return <PhoneTerminal panel={panel} fetchedAt={fetchedAt} now={now} error={error} refetch={refresh} stream={stream}
    watch={watch} watchError={watchError} accounts={accounts} account={account} onSwitchAccount={onSwitch} />;
  return <RiskTerminal rootRef={rootRef} panel={panel} fetchedAt={fetchedAt} now={now} error={error} refetch={refresh} stream={stream}
    watch={watch} watchError={watchError} refreshWatch={refreshWatch}
    accounts={accounts} account={account} onSwitchAccount={onSwitch} />;
}

export default function RiskTerminalScreen() {
  const [list, setList] = useState(null);                  // { user_key, accounts }
  const [failed, setFailed] = useState(null);
  const [account, setAccount] = useState(null);

  useEffect(() => {
    let alive = true;
    fetchAccounts().then(r => {
      if (!alive) return;
      if (!r.ok) { setFailed(r.status === 401 ? 'signed out' : 'HTTP ' + r.status); return; }
      const first = chooseInitial(r.data.accounts, readRemembered(r.data.user_key));
      setActiveAccount(first ? first.id : null);          // before the live part mounts and opens the stream
      setList(r.data);
      setAccount(first);
    });
    return () => { alive = false; setActiveAccount(null); };
  }, []);

  const onSwitch = useCallback(a => {
    setActiveAccount(a.id);
    if (list) writeRemembered(list.user_key, a.id);
    setAccount(a);
  }, [list]);

  if (failed) return <RiskTerminal panel={null} error={failed} now={Date.now()} />;
  if (!list || !account) return <RiskTerminal panel={null} now={Date.now()} />;
  return <Live key={account.id == null ? 'own' : account.id} accounts={list.accounts} account={account} onSwitch={onSwitch} />;
}
